export type SeriesPoint = {
  mesureId: string;
  periodStart: string;
  value: number;
  unit: string;
  territoryId: string;
  territoryLabel: string;
  dimensions: Array<{ type: string; code: string; label: string }>;
};

export function toLandCoverChart(points: readonly SeriesPoint[]) {
  const years = [...new Set(points.map((p) => p.periodStart.slice(0, 4)))].sort();
  const classes = [
    ...new Set(
      points
        .map((p) => p.dimensions.find((d) => d.type === "classe_occupation_sol")?.label)
        .filter(Boolean),
    ),
  ] as string[];
  const byYear = years.map((year) => {
    const row: Record<string, string | number> = { year };
    for (const cls of classes) {
      const hit = points.find(
        (p) =>
          p.periodStart.startsWith(year) &&
          p.dimensions.some((d) => d.type === "classe_occupation_sol" && d.label === cls),
      );
      if (hit) row[cls] = Number(hit.value.toFixed(2));
    }
    return row;
  });
  return { years, classes, byYear, points };
}

export type LandCoverYearShare = {
  readonly name: string;
  readonly value: number;
};

/**
 * Convert one year of the area-chart series into % shares of that year’s total.
 * Keeps `classKeys` order so donut colors match the linear/area legend.
 */
export function toLandCoverYearShares(
  byYear: readonly Record<string, string | number>[],
  year: string | number,
  options?: {
    readonly classKeys?: readonly string[];
    readonly maxDirect?: number;
  },
): { segments: LandCoverYearShare[]; totalPct: number; year: string } {
  const yearKey = String(year);
  const row = byYear.find((r) => String(r.year) === yearKey);
  if (!row) return { segments: [], totalPct: 0, year: yearKey };

  const allKeys = Object.keys(row).filter((k) => k !== "year");
  const preferred = options?.classKeys ?? [];
  const ordered = [
    ...preferred.filter((k) => allKeys.includes(k)),
    ...allKeys.filter((k) => !preferred.includes(k)),
  ];

  const entries = ordered
    .map((name) => {
      const raw = row[name];
      const value = typeof raw === "number" ? raw : Number(raw);
      return { name, value: Number.isFinite(value) ? value : 0 };
    })
    .filter((e) => e.value > 0);

  const total = entries.reduce((acc, e) => acc + e.value, 0);
  if (total <= 0) return { segments: [], totalPct: 0, year: yearKey };

  const toPct = (value: number) => Number(((value / total) * 100).toFixed(2));
  const maxDirect = options?.maxDirect ?? (preferred.length > 0 ? preferred.length : 8);

  let segments: LandCoverYearShare[];
  if (entries.length <= maxDirect + 1) {
    segments = entries.map((e) => ({ name: e.name, value: toPct(e.value) }));
  } else {
    const head = entries.slice(0, maxDirect);
    const restSum = entries.slice(maxDirect).reduce((acc, e) => acc + e.value, 0);
    segments = [
      ...head.map((e) => ({ name: e.name, value: toPct(e.value) })),
      { name: "Autres", value: toPct(restSum) },
    ];
  }

  const totalPct = Number(segments.reduce((acc, s) => acc + s.value, 0).toFixed(2));
  return { segments, totalPct, year: yearKey };
}

/** Anthropic pressure classes that stress natural ecosystems in the BKM study. */
export type LandCoverPressureBucket = "cultures" | "habite" | "infrastructure" | "naturel";

const PRESSURE_SEGMENT_ORDER: readonly LandCoverPressureBucket[] = [
  "cultures",
  "habite",
  "infrastructure",
  "naturel",
] as const;

export const LAND_COVER_PRESSURE_LABELS: Record<LandCoverPressureBucket, string> = {
  cultures: "Zones de cultures",
  habite: "Zones habitées",
  infrastructure: "Infrastructures & sols nus",
  naturel: "Milieux naturels (savanes, forêts, zones humides)",
};

/**
 * Map a published occupation-class label to a pressure reading.
 * Cultures / habitat / infrastructure = threat; forests, savannas, wetlands = natural cover.
 */
export function landCoverPressureBucket(label: string): LandCoverPressureBucket {
  const key = label.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
  if (key.includes("culture")) return "cultures";
  if (key.includes("habit")) return "habite";
  if (
    key.includes("routier") ||
    key.includes("ferroviaire") ||
    key.includes("sols nus") ||
    key.includes("affleurement")
  ) {
    return "infrastructure";
  }
  return "naturel";
}

/**
 * Year snapshot framed as pressures on nature (not a neutral occupation pie).
 * Center metric = share of anthropic pressure classes.
 */
export function toLandCoverPressureShares(
  byYear: readonly Record<string, string | number>[],
  year: string | number,
): {
  segments: LandCoverYearShare[];
  /** Share of the territory under anthropic pressure (0–100). */
  pressurePct: number;
  naturalPct: number;
  year: string;
} {
  const yearKey = String(year);
  const row = byYear.find((r) => String(r.year) === yearKey);
  if (!row) {
    return { segments: [], pressurePct: 0, naturalPct: 0, year: yearKey };
  }

  const totals: Record<LandCoverPressureBucket, number> = {
    cultures: 0,
    habite: 0,
    infrastructure: 0,
    naturel: 0,
  };

  for (const key of Object.keys(row)) {
    if (key === "year") continue;
    const raw = row[key];
    const value = typeof raw === "number" ? raw : Number(raw);
    if (!Number.isFinite(value) || value <= 0) continue;
    totals[landCoverPressureBucket(key)] += value;
  }

  const grand = PRESSURE_SEGMENT_ORDER.reduce((acc, k) => acc + totals[k], 0);
  if (grand <= 0) {
    return { segments: [], pressurePct: 0, naturalPct: 0, year: yearKey };
  }

  const toPct = (value: number) => Number(((value / grand) * 100).toFixed(2));
  const segments = PRESSURE_SEGMENT_ORDER.filter((k) => totals[k] > 0).map((k) => ({
    name: LAND_COVER_PRESSURE_LABELS[k],
    value: toPct(totals[k]),
  }));

  const pressureRaw = totals.cultures + totals.habite + totals.infrastructure;
  const pressurePct = toPct(pressureRaw);
  const naturalPct = toPct(totals.naturel);

  return { segments, pressurePct, naturalPct, year: yearKey };
}
